/**
 * Where is this page running, as far as the mobile apps are concerned?
 *
 * Two contexts count as "the app", and both matter for what we may show and
 * collect:
 *  - inside the Capacitor shell itself (WKWebView / Android WebView);
 *  - the login leg the shell opens in an external browser
 *    (SFSafariViewController / Chrome Custom Tab): /login?from=mobile →
 *    OAuth → /?session=… → /auth/mobile. That is still the app's sign-in
 *    flow to a reviewer, even though it is served as the web.
 *
 * Kept dependency-free on purpose: it is read from the entry chunk (PostHog
 * init, the consent banner, the login page) before anything else loads.
 */

/** sessionStorage flag set when the shell's external-browser login starts, so
 *  the OAuth callback routes back to /auth/mobile. Owned here so the login
 *  page, AuthMobile, AuthenticatedApp and analytics all read ONE string. */
export const PENDING_MOBILE_HANDOFF_KEY = "ubik_mobile_handoff_pending";

/** True inside the Capacitor shell (Android or iOS). Same probe AuthContext
 *  uses; no plugin import, so the web bundle carries nothing extra. */
export function inNativeShell(): boolean {
  try {
    return window.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/** True on the external-browser login leg the shells open. */
export function isMobileHandoffLeg(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("from") === "mobile") return true;
    if (window.location.pathname.startsWith("/auth/mobile")) return true;
    return sessionStorage.getItem(PENDING_MOBILE_HANDOFF_KEY) === "1";
  } catch {
    return false;
  }
}

/** True on iPhone, iPad and Mac, in the iOS shell or any browser on them.
 *  Gates the Apple button: Sign in with Apple is what App Store 4.8 asks of
 *  the iOS app, and it is the option people on Apple hardware expect, while
 *  on Android or Windows it is one more button nobody uses. iPadOS Safari
 *  reports itself as "Macintosh", so the Mac test also covers iPads. */
export function isAppleDevice(): boolean {
  try {
    if (window.Capacitor?.getPlatform?.() === "ios") return true;
    return /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(window.navigator.userAgent);
  } catch {
    return false;
  }
}

/** Inside the shell OR on its login leg — "the app" as a store reviewer sees it. */
export function isMobileAppContext(): boolean {
  return inNativeShell() || isMobileHandoffLeg();
}
